import { useCallback, useEffect, useMemo, useState } from 'react'
import { dollarsToCurrency, formatCurrency } from '@/utils/currencyHelpers'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { usePrevious } from '@/utils/sharedHooks'
import { useAnalyticsTrack } from '@/features/amplitude'

import {
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Bar,
  Legend,
} from 'recharts'
import { DateTime } from 'luxon'

import { fetchExpensesSum, fetchIncomesSum } from '@/actions/reportActions'
import { DEFAULT_YEAR, YEAR_OPTIONS } from '@/constants/businessConstants'
import { Card, Select, Text, Separator, CardColors } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'

// TODO: Migrate to V2
import { Colors, Fonts } from '../../../styles/theme'

const labelFormatter = (value: string) => {
  return `$${Number(value).toFixed(2)}`
}

const legendFormatter = (value: string, _entry: unknown) => {
  return <span className={TextColors.MUTED}>{value}</span>
}

export const getInitialMonth = (currentMonth: string | undefined) =>
  currentMonth
    ? DateTime.fromFormat(currentMonth, DATE_FORMATS_LUXON.MONTH_YEAR)
    : DateTime.now().startOf('month')

interface Props {
  currentMonth?: string
  dropdownDisabled?: boolean
  monthView?: boolean
  narrow?: boolean
}

export const CashflowBarGraph = ({
  currentMonth,
  dropdownDisabled,
  monthView,
  narrow,
}: Props) => {
  const prevCurrentMonth = usePrevious(currentMonth)

  const [month, setMonth] = useState(getInitialMonth(currentMonth))
  const [year, setYear] = useState(DEFAULT_YEAR)
  const [income, setIncome] = useState(0)
  const [expenses, setExpenses] = useState(0)

  const months = useMemo(() => {
    const earliest = DateTime.fromFormat(
      'January 2020',
      DATE_FORMATS_LUXON.MONTH_YEAR_LONG
    )
    const latest = DateTime.now()
    let interim = earliest
    const timeValues = []
    while (interim < latest) {
      timeValues.push({
        text: interim.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR),
        value: interim.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR),
      })
      interim = interim.plus({ months: 1 })
    }
    return timeValues
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      const params = monthView ? { month } : { year }

      const expenses = await fetchExpensesSum(params)()
      setExpenses(expenses?.sum || 0)
      const income = await fetchIncomesSum(params)()
      setIncome(income?.sum || 0)
    }

    fetchData()
  }, [month, monthView, year])

  useEffect(() => {
    if (prevCurrentMonth !== currentMonth) {
      setMonth(getInitialMonth(currentMonth))
    }
  }, [prevCurrentMonth, currentMonth])

  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  const track = useAnalyticsTrack()

  const handleDropdownChange = useCallback(
    (value: string) => {
      const date = DateTime.fromFormat(value, DATE_FORMATS_LUXON.MONTH_YEAR)
      if (monthView) {
        setMonth(date)
      } else {
        setYear(value)
      }
      track('clicked annual bk report year', {
        graph_name: 'Annual Cash Summary',
        report_year: value,
      })
    },
    [monthView, track]
  )

  const header = useMemo(
    () => (
      <div
        className={
          !narrow && !isMobile
            ? 'flex flex-col md:flex-row items-center justify-between gap-3'
            : ''
        }
      >
        {isMobile && !dropdownDisabled && (
          <Select
            options={monthView ? months : YEAR_OPTIONS}
            onValueChange={(value) =>
              handleDropdownChange(value?.toString() || '')
            }
            value={
              monthView ? month.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR) : year
            }
          />
        )}
        <Text variant={TypographyStyles.DISPLAY_XXS} className="w-10/12">
          {monthView ? 'Monthly' : 'Annual'} Cash Summary
        </Text>
        {!isMobile && !dropdownDisabled && (
          <div className="w-2/12">
            <Select
              options={monthView ? months : YEAR_OPTIONS}
              onValueChange={(value) =>
                handleDropdownChange(value?.toString() || '')
              }
              value={
                monthView ? month.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR) : year
              }
            />
          </div>
        )}
      </div>
    ),
    [
      dropdownDisabled,
      narrow,
      month,
      monthView,
      months,
      year,
      isMobile,
      handleDropdownChange,
    ]
  )

  const profitValue = useMemo(
    () => formatCurrency(dollarsToCurrency(expenses).add(income)),
    [expenses, income]
  )

  return (
    <Card className="w-full sm:p-6" color={CardColors.LEVEL_1}>
      {header}
      <Separator className="my-2" />
      <Text variant={TypographyStyles.H2} className="mt-4">
        Profit
      </Text>
      <Text
        variant={TypographyStyles.H1}
        color={TextColors.BRAND}
        className="my-2"
      >
        <CurrencyFormatLabel value={profitValue} />
      </Text>
      <ResponsiveContainer width="100%" height={150}>
        <BarChart
          data={[
            {
              name: '',
              expenses: Math.abs(expenses) || 0,
              income: income || 0,
            },
          ]}
          margin={{ top: 0, right: 80, left: 0, bottom: 0 }}
          layout="vertical"
        >
          <XAxis type="number" hide />
          <YAxis tickLine={false} type="category" dataKey="name" hide />
          <Bar
            dataKey="income"
            fill={Colors.greenBlue}
            name="Income"
            label={{
              position: 'right',
              formatter: labelFormatter,
              // For whatever reason, the `fontFamily` property isn't being passed through
              // when we just assign `Fonts.bodySm` to `style` or even spread it, so we have
              // to explicitly define it in the definition here and below.
              style: { ...Fonts.bodySm, fontFamily: 'Inter' },
            }}
          />
          <Bar
            dataKey="expenses"
            fill={Colors.yellowOrange}
            name="Expenses"
            label={{
              position: 'right',
              formatter: labelFormatter,
              style: { ...Fonts.bodySm, fontFamily: 'Inter' },
            }}
          />
          <Legend
            align="left"
            iconType="square"
            layout="horizontal"
            formatter={legendFormatter}
          />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  )
}
